import useCreateFolder from '@/api/mutations/folders/useCreateFolder';
import useUpdateFolder from '@/api/mutations/folders/useUpdateFolder';
import { TFolder } from '@/api/queries/project/useGetProjectFolders';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Field,
  Form,
  Modal,
  ModalProps,
  RawInput,
  useToastContext,
} from '@payaca/ambrosia';

import { VALID_FOLDER_NAME } from '@payaca/constants/regexConstants';
import { useEffectOnce } from '@payaca/hooks';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod/v4';

export type TProps = {
  projectId?: string;
  workingFolder?: TFolder;
  folder?: TFolder;
} & Pick<ModalProps, 'isOpen' | 'onClose'>;

const createEditFolderSchema = z.object({
  name: z
    .string()
    .min(1)
    .max(255)
    .regex(
      VALID_FOLDER_NAME,
      'Folder names can only contain alphanumeric characters, spaces, hyphens and underscores'
    ),
});

type TFormValues = z.infer<typeof createEditFolderSchema>;

const CreateEditFolderModalContent: FC<
  Pick<TProps, 'onClose'> & {
    onSubmit: (values: TFormValues) => void;
    defaultValues?: TFormValues;
    isProgressing?: boolean;
  }
> = (props) => {
  const { onClose, onSubmit, defaultValues, isProgressing } = props;

  const formMethods = useForm<TFormValues>({
    resolver: zodResolver(createEditFolderSchema),
    defaultValues,
  });
  const {
    register,
    formState: { isDirty },
  } = formMethods;

  useEffectOnce(() => {
    formMethods.setFocus('name');
  });

  return (
    <Form<TFormValues> methods={formMethods} onSubmit={onSubmit}>
      <Modal.Body>
        <Field name="name">
          <Field.Label>Folder name</Field.Label>

          <RawInput {...register('name')} />
        </Field>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!isDirty}
            isProcessing={isProgressing}
          >
            {defaultValues ? 'Rename' : 'Create'}
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Form>
  );
};

const CreateEditFolderModal: FC<TProps> = (props) => {
  const { projectId, workingFolder, folder, isOpen, onClose } = props;

  /**
   * state
   */
  const { pushError } = useToastContext();

  /**
   * mutations
   */
  const { mutateAsync: createFolderMutation, isPending: isCreatingFolder } =
    useCreateFolder({
      projectId,
    });

  const { mutateAsync: updateFolderMutation, isPending: isUpdatingFolder } =
    useUpdateFolder({
      projectId,
    });

  /**
   * callbacks
   */
  const handleSubmit = async (values: TFormValues) => {
    try {
      if (folder?.id) {
        await updateFolderMutation({ id: folder.id, name: values.name });
      } else if (workingFolder) {
        await createFolderMutation({
          name: values.name,
          parentId: workingFolder.id,
        });
      } else {
        throw new Error('trying to create folder without a parent folder');
      }

      onClose?.();
    } catch (error) {
      console.error('Failed to rename folder:', error);
      pushError('Failed to create/edit folder. Please try again.');
    }
  };

  return (
    <Modal
      title={folder ? 'Rename folder' : 'Create folder'}
      isOpen={isOpen}
      onClose={onClose}
    >
      <CreateEditFolderModalContent
        onSubmit={handleSubmit}
        onClose={onClose}
        isProgressing={isCreatingFolder || isUpdatingFolder}
        defaultValues={
          folder
            ? {
                name: folder.name,
              }
            : undefined
        }
      />
    </Modal>
  );
};

export default CreateEditFolderModal;
